/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Choose what to put on a dashboard.
 *
 * The main path is "an existing report chart", because that is the reuse the
 * whole module is built around: build a chart once on a report and drop it on
 * any number of dashboards. Only reports the caller can see are listed — the
 * backend re-checks that on save, and again on every run, so a share revoked
 * afterwards stops the tile rather than leaving a hole in the ACL.
 */

import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Autocomplete, Box, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, TextField, Typography,
} from '@mui/material';
import {
  BarChartRounded, NotesRounded, SpeedRounded, TableRowsRounded,
  WavingHandRounded,
} from '@mui/icons-material';

import MDButton from 'components/MDButton';
import { useAuth } from 'auth/Auth';
import colors from 'styles/tokens/colors';

const KINDS = [
  { value: 'report_chart', label: 'Chart', icon: BarChartRounded,
    hint: 'A chart you already built on a report.' },
  { value: 'kpi', label: 'Number', icon: SpeedRounded,
    hint: 'One figure from a report — a total, a count.' },
  { value: 'list', label: 'List', icon: TableRowsRounded,
    hint: 'The top rows of a report, as a table.' },
  { value: 'text', label: 'Note', icon: NotesRounded,
    hint: 'A heading or a note for whoever reads this.' },
  { value: 'greeting', label: 'Greeting', icon: WavingHandRounded,
    hint: 'A welcome banner with the date.' },
];

export default function AddWidgetDialog({ open, onClose, onAdd, existingKeys }) {
  const { axiosInstance } = useAuth();

  const [kind, setKind] = useState('report_chart');
  const [reports, setReports] = useState([]);
  const [report, setReport] = useState(null);
  const [chartId, setChartId] = useState('');
  const [valueKey, setValueKey] = useState('');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    axiosInstance.get('/analytics/reports', { params: { limit: 500 } })
      .then(({ data }) => setReports(Array.isArray(data) ? data : []))
      .catch(() => setReports([]));
  }, [open, axiosInstance]);

  // A chart widget can only use a report that has charts; offering the rest
  // leads to picking one and finding the chart list empty.
  const options = useMemo(
    () => (kind === 'report_chart'
      ? reports.filter((r) => r.charts?.length)
      : reports),
    [reports, kind],
  );

  const charts = report?.charts ?? [];
  const selectedChart = charts.find((c) => c.id === chartId);

  const reset = () => {
    setReport(null); setChartId(''); setValueKey(''); setTitle(''); setText('');
  };

  const submit = async () => {
    setSaving(true);
    try {
      await onAdd(buildWidget({ kind, report, chartId, selectedChart, valueKey,
                                title, text, existingKeys }));
      reset();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const ready =
    kind === 'text' ? text.trim().length > 0
      : kind === 'greeting' ? true
        : kind === 'report_chart' ? Boolean(chartId)
          : Boolean(report);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>Add a widget</DialogTitle>
      <DialogContent dividers>
        <Box display="grid" gap={1} gridTemplateColumns="repeat(auto-fit, minmax(110px, 1fr))"
             mb={2.5}>
          {KINDS.map(({ value, label, icon: Icon }) => (
            <Box
              key={value}
              onClick={() => { setKind(value); reset(); }}
              sx={{
                p: 1.25, textAlign: 'center', cursor: 'pointer', borderRadius: '12px',
                border: '1px solid',
                borderColor: kind === value ? colors.brand.primary : 'divider',
                backgroundColor: kind === value
                  ? colors.brand.primaryContainer : 'transparent',
                color: kind === value ? colors.brand.primary : colors.text.dark,
              }}
            >
              <Icon sx={{ fontSize: 20 }} />
              <Typography variant="caption" display="block" fontWeight={600}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          {KINDS.find((k) => k.value === kind)?.hint}
        </Typography>

        {(kind === 'report_chart' || kind === 'kpi' || kind === 'list') && (
          <>
            <Autocomplete
              size="small" options={options} value={report}
              onChange={(_, value) => { setReport(value); setChartId(''); setValueKey(''); }}
              getOptionLabel={(o) => o?.name ?? ''}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              renderInput={(params) => <TextField {...params} label="Report" />}
              noOptionsText={kind === 'report_chart'
                ? 'No reports have charts yet — open a report and add one first'
                : 'No reports available'}
              sx={{ mb: 2 }}
            />

            {kind === 'report_chart' && report && (
              <TextField
                select size="small" fullWidth label="Chart" value={chartId}
                onChange={(e) => setChartId(e.target.value)} sx={{ mb: 2 }}
              >
                {charts.map((chart) => (
                  <MenuItem key={chart.id} value={chart.id}>
                    {chart.name} ({chart.chart_type.replace('_', ' ')})
                  </MenuItem>
                ))}
              </TextField>
            )}

            {kind === 'kpi' && report && (
              <>
                <TextField
                  size="small" fullWidth label="Measure key" value={valueKey}
                  onChange={(e) => setValueKey(e.target.value)} sx={{ mb: 1 }}
                  placeholder="sum__value"
                  helperText="Leave blank to use the report's first summary, or its row count."
                />
                {!report.definition?.aggregates?.length && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This report has no summary, so the tile will show how many
                    records it returns.
                  </Alert>
                )}
              </>
            )}
          </>
        )}

        {kind === 'text' && (
          <TextField
            size="small" fullWidth multiline minRows={3} label="Text"
            value={text} onChange={(e) => setText(e.target.value)} sx={{ mb: 2 }}
          />
        )}

        <TextField
          size="small" fullWidth label="Title (optional)" value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={selectedChart?.name ?? report?.name ?? ''}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <MDButton variant="text" color="dark" onClick={onClose}>Cancel</MDButton>
        <MDButton variant="gradient" color="info" onClick={submit}
                  disabled={!ready || saving}>
          {saving ? 'Adding…' : 'Add to dashboard'}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

AddWidgetDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  existingKeys: PropTypes.array,
};

/**
 * Build the widget payload.
 *
 * grid_key is generated here and must be unique per dashboard: it is what the
 * stored layout references, and reusing one would make two tiles fight for the
 * same cell. An id cannot serve, because it is not known until after the save
 * and is not stable across a delete-and-re-add.
 */
function buildWidget({ kind, report, chartId, selectedChart, valueKey, title,
                       text, existingKeys }) {
  const used = new Set(existingKeys ?? []);
  let index = used.size;
  let gridKey = `${kind}-${index}`;
  while (used.has(gridKey)) { index += 1; gridKey = `${kind}-${index}`; }

  const config = {};
  if (title.trim()) config.title = title.trim();
  if (kind === 'text') config.text = text;
  if (kind === 'kpi' && valueKey.trim()) config.value_key = valueKey.trim();
  if (kind === 'list') config.limit = 20;

  return {
    widget_kind: kind,
    grid_key: gridKey,
    report_chart_id: kind === 'report_chart' ? chartId : null,
    report_id: kind === 'kpi' || kind === 'list' ? report?.id ?? null : null,
    config,
    order_index: index,
  };
}
